import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { AdminClient, TransferLogPage } from '@baex/shared';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { transfers } from '../../api/endpoints';
import { formatBytes, formatCount } from '../../lib/format';
import { useTimeFormat } from '../../lib/timezone';
import { displayFileName, statusLabel } from '../../lib/transferLabels';

/** The "Client list — table and row actions" section: "a small preview of that client's most recent transfer-history entries". */
export const HISTORY_PREVIEW_LIMIT = 5;

export const NO_HISTORY_TEXT = 'No transfers recorded for this client yet.';

/**
 * The "Client list — table and row actions" section, "Which API key a client currently holds": the key is shown masked with only
 * its last four characters legible. Those four are recorded at issue time alongside the
 * hash — never derived from it — and the full key is still never stored.
 */
export const KEY_MASK = '••••••••••••••••';

/** A key issued before the hint was recorded: the plaintext is gone and cannot be recovered. */
export const NO_KEY_HINT_TEXT = 'Not recorded — rotate the key to capture it.';

export function maskApiKey(hint: string): string {
  return `${KEY_MASK}${hint}`;
}

/**
 * The preview reads the same endpoint the Transfers screen reads, narrowed to this client
 * (`?client=…&limit=5`). `client` matches rows where the client is either the acting client
 * or the folder owner, which is exactly the history this dialog should show.
 *
 * It is a separate query on purpose: the facts above render immediately whether or not the
 * history has arrived, and this section carries its own loading and empty states.
 */
function HistoryPreview({ clientId }: { clientId: string }) {
  const time = useTimeFormat();
  const history = useQuery<TransferLogPage>({
    queryKey: ['client-history-preview', clientId],
    queryFn: ({ signal }) =>
      transfers.list({ client: clientId, limit: HISTORY_PREVIEW_LIMIT, offset: 0 }, signal),
  });

  const entries = history.data?.entries ?? [];

  return (
    <section aria-label="Recent transfer history" className="mt-4">
      <h3 className="text-2xs uppercase tracking-wide text-ink-subtle">Recent transfers</h3>

      {history.isLoading ? (
        <p role="status" className="mt-1.5 text-[12px] text-ink-muted">
          Loading…
        </p>
      ) : null}

      {history.isError ? (
        <p className="mt-1.5 text-[12px] text-ink-muted">
          This client&apos;s recent history could not be loaded.
        </p>
      ) : null}

      {!history.isLoading && !history.isError && entries.length === 0 ? (
        <p className="mt-1.5 text-[12px] text-ink-muted">{NO_HISTORY_TEXT}</p>
      ) : null}

      {entries.length > 0 ? (
        <ul className="mt-1.5 flex flex-col gap-1">
          {entries.map((entry) => (
            <li key={entry.id} className="flex gap-3 text-[12px] text-ink">
              <span
                className="tabular shrink-0 text-ink-muted"
                title={time.rawTimestampTitle(entry.time)}
              >
                {time.formatTableTime(entry.time)}
              </span>
              <span className="shrink-0 text-ink-muted">{entry.op}</span>
              <span className="shrink-0 text-ink-muted">{statusLabel(entry)}</span>
              {/* Client-supplied text — plain text binding, never markup (see the "Table" section). */}
              <span className="min-w-0 flex-1 truncate">{displayFileName(entry)}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/**
 * Read-only client detail (see the "Client list — table and row actions" section, "View"): "No editable fields here — Client ID and
 * scope are fixed at creation and display name/notes editing isn't part of this version."
 * Notes are shown as text; the API accepts them on PATCH, but nothing here calls it.
 * The history preview below is read-only too: no sorting, no filtering, no row expansion.
 */
interface Fact {
  label: string;
  value: ReactNode;
  /** Optional `title` on the value cell — the raw timestamp behind a formatted date. */
  title?: string | undefined;
}

function ClientDetail({ client, onClose }: { client: AdminClient; onClose: () => void }) {
  const time = useTimeFormat();

  const facts: Fact[] = [
    { label: 'Client ID', value: client.clientId },
    { label: 'Display name', value: client.displayName },
    {
      label: 'Scope',
      value: client.scope === 'own' ? 'own (its own folder only)' : 'any (every folder)',
    },
    { label: 'Status', value: client.status },
    // The key it currently holds: masked, plus the date that key was issued (see the "Client list — table and row actions" section).
    // Plain text on purpose — this is not a credential, so it gets no copy control.
    {
      label: 'API key',
      value: client.apiKeyHint ? (
        <span className="flex flex-wrap items-baseline gap-x-2">
          <span>{maskApiKey(client.apiKeyHint)}</span>
          <span className="text-ink-muted">issued {time.formatDay(client.keyIssuedAt)}</span>
        </span>
      ) : (
        <span className="text-ink-muted">{NO_KEY_HINT_TEXT}</span>
      ),
      title: client.apiKeyHint ? time.rawTimestampTitle(client.keyIssuedAt) : undefined,
    },
    { label: 'Created', value: time.formatDay(client.createdAt) },
    {
      label: 'Last successful call',
      value: client.lastUsedAt ? time.formatTimestamp(client.lastUsedAt) : 'never',
      title: time.rawTimestampTitle(client.lastUsedAt),
    },
    { label: 'Files waiting', value: formatCount(client.filesWaiting) },
    { label: 'Size waiting', value: formatBytes(client.bytesWaiting) },
    // Client-supplied text — rendered as literal text, never markup (see the "Table" section).
    { label: 'Notes', value: client.notes.trim().length > 0 ? client.notes : '—' },
  ];

  return (
    <Modal
      open
      title={client.clientId}
      onClose={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <dl className="flex flex-col gap-1.5">
        {facts.map(({ label, value, title }) => (
          <div key={label} className="flex gap-3">
            <dt className="w-40 shrink-0 text-2xs uppercase tracking-wide text-ink-subtle">
              {label}
            </dt>
            <dd
              className="tabular min-w-0 flex-1 whitespace-pre-wrap break-words text-[12px] text-ink"
              title={title}
            >
              {value}
            </dd>
          </div>
        ))}
      </dl>

      <HistoryPreview clientId={client.clientId} />

      <p className="mt-4 text-2xs text-ink-muted">
        Nothing on this screen is editable: Client ID and scope are fixed at creation, and display
        name and notes editing is not part of this version.
      </p>
      <Link
        to={`/transfers?client=${encodeURIComponent(client.clientId)}`}
        onClick={onClose}
        className="mt-3 inline-block text-[12px] text-primary underline"
      >
        View this client&apos;s transfer history
      </Link>
    </Modal>
  );
}

export function ClientDetailModal({
  client,
  onClose,
}: {
  client: AdminClient | null;
  onClose: () => void;
}) {
  if (!client) return null;
  return <ClientDetail client={client} onClose={onClose} />;
}
